import React, { MutableRefObject, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { css } from '@emotion/css';
import classNames from 'classnames';
import Button from '@prezzee/ribbon-ui/lib/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { DEFAULT_FILTER_CURRENT, DrainContext, DrainType } from 'contexts/DrainContext';
import { EMPTY_ELEMENT } from 'common/utils/react';
import { FilterTaxonomy } from 'api/queries/filter';

import { FilterDefault } from './filter/FilterDefault';
import { FilterItem } from './filter/FilterItem';

import { Color } from 'css/theme';

const classes = {
  root: (open: boolean) => css`
    position: fixed;
    overflow-y: scroll;
    inset: 0;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background-color: #ffffff;
    display: flex;
    visibility: ${open ? 'visible' : 'hidden'};
    flex-direction: column;
    justify-content: space-between;
    z-index: 999;
    color: ${Color.Gray800};
    font-size: 15px;
    transition: transform 0.5s ease, visibility 0.5s ease;
    transform: translateY(100%);
  `,
  activeRoot: css`
    transform: translateY(0) !important;
    transition: transform 0.5s ease, visibility 0.5s ease;
    visibility: visible;
  `,
  overlay: (open: boolean) => css`
    width: 100%;
    height: 100%;
    z-index: ${open ? 99 : 0};
    position: fixed;
    top: ${open ? 0 : 'auto'};
    left: ${open ? 0 : 'auto'};
    background-color: rgba(0, 0, 0, ${open ? 0.4 : 0});
  `,
  header: {
    root: css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 57px;
      padding: 13px 20px 10px 20px;
      border: 1px solid ${Color.Gray100};
    `,
    back: {
      root: css`
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
      `,
      image: css`
        margin-top: 5px;
      `,
      text: css`
        padding-left: 10px;
      `,
    },
    default: {
      root: css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      `,
      title: css`
        display: flex;
        flex-direction: row;
        font-weight: 600;
      `,
      action: {
        item: css`
          color: ${Color.Purple400};
          cursor: pointer;
        `,
        title: css`
          cursor: pointer;
        `,
      },
      marker: {
        root: css`
          background-color: ${Color.Blue400};
          height: 18px;
          width: 18px;
          border-radius: 50%;
          margin-left: 10px;
          margin-top: 4px;
        `,
        item: css`
          font-size: 14px;
          padding-left: 5px;
          padding-top: 0;
          color: #ffffff;
          font-weight: 600;
          line-height: 100%;
        `,
      },
    },
  },
  content: {
    root: css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      height: 100%;
      overflow-y: auto;
    `,
  },
  footer: {
    root: css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      box-shadow: 0px -2px 40px rgba(0, 0, 0, 0.29);
      padding-top: 15px;
    `,
    close: css`
      width: 50%;
      padding: 0 5px 10px 10px;
    `,
    view: css`
      width: 50%;
      padding: 0 10px 10px 5px;
    `,
  },
};

type Props = {
  open: boolean;
  filterList?: FilterTaxonomy;
  scrollRef?: MutableRefObject<HTMLHeadingElement>;
};

export const DrainSection: React.FC<Props> = ({ filterList, open }) => {
  const drain = useContext(DrainContext);
  const router = useRouter();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'hidden';
    }
    router.events.on('routeChangeStart', () => {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = '';
    });
    return () => {
      if (!open) {
        document.body.style.overflow = 'auto';
        document.body.style.touchAction = '';
      }
    };
  }, [open]);

  return (
    <>
      <Head>
        <style>
          {`@media (max-width: 1287px) {
            #ada-button-frame {
              bottom: ${open ? '52px' : '0'} !important;
            }
          }`}
        </style>
      </Head>
      <div className={classes.overlay(open)} />
      <div className={classNames([classes.root(open), open ? classes.activeRoot : void 0])}>
        <div className={classes.header.root}>
          {drain.page.parent ? (
            <div className={classes.header.back.root}>
              <FontAwesomeIcon icon={['fas', 'angle-left']} className={classes.header.back.image} />
              <span
                className={classes.header.back.text}
                onClick={() => {
                  drain.setPage(drain.page.parent);
                }}
              >
                Back to Filters
              </span>
            </div>
          ) : (
            <div className={classes.header.default.root}>
              <div className={classes.header.default.title}>
                <span>
                  {
                    {
                      [DrainType.Filter]: 'Filters',
                      [DrainType.Sorting]: 'Sorting',
                    }[drain.currentType]
                  }
                </span>
                {drain.filter.elements.length > 0 ? (
                  <div className={classes.header.default.marker.root}>
                    <div className={classes.header.default.marker.item}>{drain.filter.elements.length}</div>
                  </div>
                ) : (
                  EMPTY_ELEMENT
                )}
              </div>
              <div className={classes.header.default.action.item}>
                {/* @todo: define the activity */}
                <span
                  className={classes.header.default.action.title}
                  onClick={() => {
                    document.body.style.overflow = 'unset';
                    drain.switchFilter({
                      clear: true,
                      apply: true,
                      sorting: {
                        current: drain.sort,
                        previous: drain.prevSort,
                      },
                    });
                    drain.trackDrainFilterSection(DEFAULT_FILTER_CURRENT, drain.search);
                  }}
                >
                  {filterList.root.clearAll.title}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className={classes.content.root}>
          {
            drain.currentType === DrainType.Filter ? (
              drain.page.code === 'default' ? (
                <FilterDefault list={filterList} />
              ) : (
                <FilterItem code={drain.page.code} list={filterList} />
              )
            ) : (
              EMPTY_ELEMENT
            ) //
          }
        </div>
        <div className={classes.footer.root}>
          <div className={classes.footer.close}>
            <Button
              type="submit"
              variant="secondary"
              fullWidth
              onClick={() => {
                document.body.style.overflow = 'unset';
                drain.switchFilter({
                  apply: false,
                  close: true,
                });
              }}
            >
              {filterList.root.close.title}
            </Button>
          </div>
          <div className={classes.footer.view}>
            <Button
              type="submit"
              fullWidth
              onClick={() => {
                document.body.style.overflow = 'unset';
                drain.switchFilter({
                  apply: true,
                  sorting: {
                    current: drain.sort,
                    previous: drain.prevSort,
                  },
                });
                drain.trackDrainFilterSection(drain.filter, drain.search);
              }}
            >
              {filterList.root.viewResults.title}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
